import { MDText } from 'i18n-react';

import { toast } from '@motorway/mw-highway-code/pre-alpha';

import { getConversationHistoryApi } from 'Pages/api/ai-companion/get-conversation-history/client.api';
import { listConversationsApi } from 'Pages/api/ai-companion/list-conversations/client.api';
import { sendUserQueryApi } from 'Pages/api/ai-companion/send-user-query/client.api';
import { sendUserQueryStream } from 'Pages/api/ai-companion/send-user-query/client.stream';
import {
	AI_COMPANION_WARNING_TYPES,
	triggerAiCompanionHighConversationCountLoadedEvent,
	triggerAiCompanionHistoryLoadedEvent,
	triggerAiCompanionSendQueryCTAButtonClickedEvent,
	triggerAiCompanionUnsuccessfulMessageShownEvent,
} from 'Services/analytics/events/aiCompanionEvents';
import { logger } from 'Services/logger/logger';
import { CONVERSATION_TITLE_MAX_LENGTH, STALE_TURN_THRESHOLD } from 'Utilities/consts';

import AiCompanionTexts from '../AiCompanion.texts';

import { cancelPendingPoll, pollPendingTurn } from './pendingTurnPoller';
import { ConversationStore } from './stores';

const T = new MDText(AiCompanionTexts);

let historyAbortController: AbortController | null = null;

const cancelHistoryFetch = () => {
	if (historyAbortController) {
		historyAbortController.abort();
		historyAbortController = null;
	}
};

/**
 * Load conversation history.
 * When activeConversationId is set, the server uses ListTurns via the dealer agent gateway.
 * When null, the server falls back to the legacy Bedrock memory read.
 * To be removed in DEP-1274
 */
const getConversationHistory = async () => {
	cancelHistoryFetch();
	const controller = new AbortController();
	historyAbortController = controller;

	try {
		const { activeConversationId } = ConversationStore.getState();
		const conversationHistoryParams = activeConversationId ? { conversationId: activeConversationId } : {};
		const { items, pendingLatestTurnCreatedAt } = await getConversationHistoryApi(conversationHistoryParams, {
			signal: controller.signal,
		});

		if (controller.signal.aborted) return;

		if (items?.length) {
			triggerAiCompanionHistoryLoadedEvent();
		}

		if (!pendingLatestTurnCreatedAt) {
			ConversationStore.setState({ items });
			return;
		}

		const age = Date.now() - new Date(pendingLatestTurnCreatedAt).getTime();

		if (age > STALE_TURN_THRESHOLD) {
			logger.warn({
				message: 'Pending turn stale on page load',
				scope: 'AiCompanion:getConversationHistory',
			});
			triggerAiCompanionUnsuccessfulMessageShownEvent({ warningType: AI_COMPANION_WARNING_TYPES.TIMEOUT });
			ConversationStore.setState({
				items: [
					...items,
					{ createdAt: pendingLatestTurnCreatedAt, eventId: `agent-stale-${Date.now()}`, type: 'timeout' as const },
				],
			});
			return;
		}

		ConversationStore.setState({ items, thinking: true });
		void pollPendingTurn(conversationHistoryParams, pendingLatestTurnCreatedAt);
	} catch (error) {
		if (controller.signal.aborted) return;

		triggerAiCompanionUnsuccessfulMessageShownEvent({ warningType: AI_COMPANION_WARNING_TYPES.MESSAGE_LOAD_ERROR });
		toast({
			content: T.translate('messageLoadError.description'),
			heading: T.translate('messageLoadError.title'),
			variant: 'error',
		});
		logger.error({
			error: error as Error,
			message: 'Failed to fetch conversation items',
			scope: 'AiCompanion:getConversationItems',
		});
	} finally {
		if (historyAbortController === controller) {
			historyAbortController = null;
		}
	}
};

const sendUserRequest = async (content: string) => {
	cancelPendingPoll();
	try {
		const eventId = new Date().toISOString();
		ConversationStore.setState(({ items }) => ({
			items: [...items, { content, createdAt: eventId, eventId, type: 'user' }],
			thinking: true,
		}));

		const { activeConversationId: originConversationId } = ConversationStore.getState();

		// Optimistically add a placeholder conversation when starting a new chat
		const optimisticId = originConversationId ? null : `optimistic-${Date.now()}-${Math.random()}`;
		if (optimisticId) {
			ConversationStore.setState(({ conversations }) => ({
				activeConversationId: optimisticId,
				conversations: [
					{
						conversationId: optimisticId,
						createdAt: eventId,
						lastMessageAt: eventId,
						title: content.slice(0, CONVERSATION_TITLE_MAX_LENGTH),
						turnCount: 1,
					},
					...conversations,
				],
			}));
		}

		const response = await sendUserQueryApi({ conversationId: originConversationId, query: content });
		const { conversationId, item } = response;

		// If the user switched conversations while waiting, discard the UI update
		const { activeConversationId: currentActiveId } = ConversationStore.getState();
		const expectedId = optimisticId ?? originConversationId;
		if (currentActiveId !== expectedId && currentActiveId !== conversationId) {
			return;
		}

		// Replace the optimistic conversation with the real one, or store a new one
		if (conversationId && (optimisticId || conversationId !== originConversationId)) {
			ConversationStore.setState(({ conversations }) => ({
				activeConversationId: conversationId,
				conversations: conversations.map((c) =>
					c.conversationId === (optimisticId ?? originConversationId)
						? {
								...c,
								conversationId,
								lastMessageAt: new Date().toISOString(),
							}
						: c,
				),
			}));
		}

		ConversationStore.setState(({ items }) => ({
			items: [...items, item],
		}));

		// Update lastMessageAt on the active conversation so it sorts to the top
		const currentConversationId = ConversationStore.getState().activeConversationId;
		if (currentConversationId) {
			ConversationStore.setState(({ conversations }) => ({
				conversations: conversations.map((c) =>
					c.conversationId === currentConversationId ? { ...c, lastMessageAt: new Date().toISOString() } : c,
				),
			}));
		}

		triggerAiCompanionSendQueryCTAButtonClickedEvent({
			agentResponse: item.content || '',
			prompt: content,
			success: true,
		});
	} catch (error) {
		triggerAiCompanionSendQueryCTAButtonClickedEvent({
			agentResponse: '',
			prompt: content,
			success: false,
		});
		toast({
			content: T.translate('sendQueryError.description'),
			heading: T.translate('sendQueryError.title'),
			variant: 'error',
		});
		logger.error({
			error: error as Error,
			message: 'Failed to send user request',
			scope: 'AiCompanion:sendUserRequest',
		});
	} finally {
		ConversationStore.setState({ thinking: false });
	}
};

const activeStreamControllers = new Set<AbortController>();

const cancelActiveStreams = () => {
	activeStreamControllers.forEach((controller) => controller.abort());
	activeStreamControllers.clear();
};

const sendUserRequestStream = async (content: string) => {
	cancelPendingPoll();

	const controller = new AbortController();
	activeStreamControllers.add(controller);

	const eventId = new Date().toISOString();

	ConversationStore.setState(({ items }) => ({
		items: [...items, { content, createdAt: eventId, eventId, type: 'user' }],
		thinking: true,
	}));

	const { activeConversationId: originConversationId } = ConversationStore.getState();

	try {
		await sendUserQueryStream({
			callbacks: {
				onClose: () => {
					const { activeConversationId } = ConversationStore.getState();
					const streamConversationId = originConversationId ?? activeConversationId;

					if (activeConversationId === streamConversationId) {
						triggerAiCompanionUnsuccessfulMessageShownEvent({ warningType: AI_COMPANION_WARNING_TYPES.ERROR });
						toast({
							content: T.translate('sendQueryError.description'),
							heading: T.translate('sendQueryError.title'),
							variant: 'error',
						});
						ConversationStore.setState({ thinking: false });
					}
				},
				onConversationId: ({ conversationId, isNewConversation }) => {
					if (isNewConversation) {
						const { activeConversationId: currentActiveId } = ConversationStore.getState();

						const userStillViewingConversation = currentActiveId === originConversationId;

						ConversationStore.setState(({ conversations }) => ({
							...(userStillViewingConversation ? { activeConversationId: conversationId } : {}),
							conversations: [
								{
									conversationId,
									createdAt: new Date().toISOString(),
									lastMessageAt: new Date().toISOString(),
									title: content.slice(0, CONVERSATION_TITLE_MAX_LENGTH),
									turnCount: 1,
								},
								...conversations,
							],
						}));
					}
				},
				onError: (error) => {
					triggerAiCompanionSendQueryCTAButtonClickedEvent({
						agentResponse: '',
						prompt: content,
						success: false,
					});
					toast({
						content: T.translate('sendQueryError.description'),
						heading: T.translate('sendQueryError.title'),
						variant: 'error',
					});
					logger.error({
						error,
						message: 'Failed to send user request (stream)',
						scope: 'AiCompanion:sendUserRequestStream',
					});
					ConversationStore.setState({ thinking: false });
				},
				onResponse: (data) => {
					ConversationStore.setState(({ conversations }) => ({
						conversations: conversations.map((conversation) =>
							conversation.conversationId === data.conversationId
								? { ...conversation, lastMessageAt: new Date().toISOString() }
								: conversation,
						),
					}));

					triggerAiCompanionSendQueryCTAButtonClickedEvent({
						agentResponse: data.item?.content || '',
						prompt: content,
						success: true,
					});

					const { activeConversationId: currentActiveId } = ConversationStore.getState();
					if (currentActiveId !== data.conversationId) {
						ConversationStore.setState({ thinking: false });
						return;
					}

					ConversationStore.setState(({ items }) => ({
						items: [...items, data.item],
						thinking: false,
					}));
				},
			},
			data: { conversationId: originConversationId, query: content },
			signal: controller.signal,
		});
	} catch (error) {
		// Don't log or show errors for intentionally aborted streams (e.g. component unmount)
		if (controller.signal.aborted) return;

		ConversationStore.setState({ thinking: false });
		logger.error({
			error: error as Error,
			message: 'Failed to send user request (stream)',
			scope: 'AiCompanion:sendUserRequestStream',
		});
	} finally {
		activeStreamControllers.delete(controller);
	}
};

const MAX_CONVERSATION_PAGES = 10;

const fetchAllConversations = async () => {
	const { conversations, nextCursor } = await listConversationsApi();
	let allConversations = conversations;
	let cursor = nextCursor;
	let totalPages = 1;

	while (cursor && totalPages < MAX_CONVERSATION_PAGES) {
		try {
			const { conversations: nextConversations, nextCursor: newCursor } = await listConversationsApi({
				cursor,
			});
			allConversations = [...allConversations, ...nextConversations];
			cursor = newCursor;
			totalPages += 1;
		} catch (error) {
			logger.error({
				error: error as Error,
				message: `Failed to fetch conversations page ${totalPages + 1}`,
				scope: 'AiCompanion:fetchAllConversations',
			});
			break;
		}
	}

	if (totalPages > 1) {
		triggerAiCompanionHighConversationCountLoadedEvent({
			totalConversations: allConversations.length,
		});
	}

	return { conversations: allConversations };
};

const initConversation = async ({
	isDealerAgentServiceEnabled,
	isMultiConversationEnabled,
}: {
	isDealerAgentServiceEnabled: boolean;
	isMultiConversationEnabled?: boolean;
}) => {
	if (isDealerAgentServiceEnabled) {
		ConversationStore.setState({ loading: true });
		try {
			const { conversations } = await fetchAllConversations();

			// When multi-conversation is enabled, populate the sidebar but don't auto-select.
			// When off, auto-select the latest conversation (one-per-sale-day behaviour).
			if (isMultiConversationEnabled) {
				const { activeConversationId, items } = ConversationStore.getState();

				// If items exist but no activeConversationId, the stream was aborted before
				// the conversationId event arrived. Select the latest conversation to recover.
				const conversationToLoad = activeConversationId ?? (items.length ? conversations?.[0]?.conversationId : null);

				ConversationStore.setState({
					conversations,
					items: [],
					thinking: false,
					...(conversationToLoad ? { activeConversationId: conversationToLoad } : {}),
				});

				if (conversationToLoad) {
					await getConversationHistory();
				}
			} else {
				const latestConversation = conversations?.[0];

				ConversationStore.setState({
					conversations,
					...(latestConversation ? { activeConversationId: latestConversation.conversationId } : {}),
				});

				const { activeConversationId } = ConversationStore.getState();
				if (activeConversationId) {
					await getConversationHistory();
				}
			}
		} catch (error) {
			triggerAiCompanionUnsuccessfulMessageShownEvent({
				warningType: AI_COMPANION_WARNING_TYPES.CONVERSATION_LOAD_ERROR,
			});
			toast({
				content: T.translate('conversationLoadError.description'),
				heading: T.translate('conversationLoadError.title'),
				variant: 'error',
			});
			logger.error({
				error: error as Error,
				message: 'Failed to fetch conversations',
				scope: 'AiCompanion:initConversation',
			});
		} finally {
			ConversationStore.setState({ loading: false });
		}
		return;
	}

	// Legacy path: load history from Bedrock memory - To be removed with DEP-1295
	const { items } = ConversationStore.getState();
	if (!items?.length) {
		ConversationStore.setState({ loading: true });
		await getConversationHistory();
		ConversationStore.setState({ loading: false });
	}
};

const selectConversation = async (conversationId: string) => {
	cancelPendingPoll();
	cancelHistoryFetch();
	ConversationStore.setState({ activeConversationId: conversationId, items: [], loading: true, thinking: false });
	await getConversationHistory();

	// Only clear loading if the user is still on this conversation
	if (ConversationStore.getState().activeConversationId === conversationId) {
		ConversationStore.setState({ loading: false });
	}
};

const startNewConversation = () => {
	cancelPendingPoll();
	cancelHistoryFetch();
	ConversationStore.setState({ activeConversationId: null, items: [], loading: false, thinking: false });
};

export const conversationService = {
	cancelActiveStreams,
	cancelHistoryFetch,
	cancelPendingPoll,
	fetchAllConversations,
	getConversationHistory,
	initConversation,
	selectConversation,
	sendUserRequest,
	sendUserRequestStream,
	startNewConversation,
};
